import {UIGameMapHighlightController} from './UIGameMapHighlightController'
import {GameStateEnums} from '../../../../../../shared/GameEnums'
import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../UIGameManager'
import {Sprites} from '../../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../../UIGameSpriteHandler'
import {UIGameMapHighlightParameters} from '../UIGameMapHighlightView'
import {UIGameActionConfirmationParameters} from '../../ui_game_hover_actions/UIHoverActionController'
import {HexCorner, Point} from '@colonist/game-mechanic-validators'
import {ActionState, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export class UIGameMapTileCornerHighlightController extends UIGameMapHighlightController {
    protected corners: number[]

    constructor(uiGameManager: UIGameManager) {
        super(uiGameManager)
        this.corners = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        super.onEvent(data, eventType)
        switch(eventType) {
            case UIGameEventEnums.SetCornerHighlight: this.setCorners(data); break
            default: break
        }
    }

    protected setCorners(highlightedIndexes: number[]) {
        this.corners = highlightedIndexes
        this.updateHighlights()
    }

    protected updateHighlights() {
        this.removeHighlights()

        if(this.corners.length > 0) {
            this.confirmActionController.setupInvisibleBackground(() => {
                this.startAnimation()
                this.hideBeginnerHintInformation()
            })
        }

        for(const cornerIndex of this.corners) {
            const hexCorner = this.uiGameManager.gameController.mapValidator.tileState.tileCorners[cornerIndex].hexCorner
            this.highlightCorner(hexCorner, cornerIndex)
        }

        if(this.highlightViews.length > 0) {
            this.uiGameManager.mapController.mapView.sortChildren()
            this.startAnimation()
        }
    }

    private highlightCorner(corner: HexCorner, index: number) {
        const highlightParameters = this.getCornerHighlightParameters(corner, index)
        const center = new PIXI.Point(highlightParameters.x + highlightParameters.size / 2, highlightParameters.y + highlightParameters.size / 2)
        const action = this.getActionForCornerHighlight(highlightParameters, center, index)
        this.highlight(highlightParameters, action)
    }

    protected getCornerHighlightParameters(corner: HexCorner, cornerIndex: number): UIGameMapHighlightParameters {
        const center = corner.toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2)
        const size = this.uiGameManager.mapController.mapView.cornerCircleR * 1.2 * this.circlesScaleForMobile
        const highlight: UIGameMapHighlightParameters = {
            x: center.x - size / 2,
            y: center.y - size / 2,
            size: size,
            index: cornerIndex,
        }
        return highlight
    }

    protected getActionForCornerHighlight(highlightParameters: UIGameMapHighlightParameters, p: Point, index: number): () => void {
        return () => {
            if(this.uiGameManager.gameController.currentState.turnState === TurnState.InitialPlacement) {
                const buttonSize = highlightParameters.size * 1.75 * this.buttonScaleForMobile
                const buttonX = p.x - buttonSize / 2
                const buttonY = p.y - buttonSize * 1.5
                const cancelAction = () => this.startAnimation()
                this.endAnimation()
                this.destroyBeginnerHintView()
                const confirmationParameters: UIGameActionConfirmationParameters[] = this.getConfirmationParameters()
                this.confirmActionController.showActionConfirmation(highlightParameters, buttonX, buttonY, buttonSize, cancelAction, confirmationParameters, index)
            } else {
                if(!this.isAllowedPlacementIndex(index)) return this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.BeginnerModeForceOpenGuideBubble)
                this.confirmedCornerSelection(index)
            }
        }
    }

    protected confirmedCornerSelection(index: number) {
        switch(this.uiGameManager.gameController.currentState.actionState) {
            case ActionState.PlaceCityWithDiscount:
            case ActionState.PlaceCity: this.uiGameManager.socketGameSend.confirmBuildCity(index); break
            case ActionState.PlaceSettlement: this.uiGameManager.socketGameSend.confirmBuildSettlement(index); break
            case ActionState.InitialPlacementPlaceSettlement: this.uiGameManager.socketGameSend.confirmBuildSettlement(index); break
            case ActionState.InitialPlacementPlaceCity: this.uiGameManager.socketGameSend.confirmBuildCity(index); break
            case ActionState.PlaceCityWall: this.uiGameManager.socketGameSend.confirmBuildCityWall(index); break
            case ActionState.PlaceCityWallForFree: this.uiGameManager.socketGameSend.confirmBuildCityWall(index); break
            case ActionState.PlaceKnightForFree:
            case ActionState.PlaceKnight: this.uiGameManager.socketGameSend.confirmPlaceKnight(index); break
            case ActionState.UpgradeKnightForFree:
            case ActionState.UpgradeKnight: this.uiGameManager.socketGameSend.confirmUpgradeKnight(index); break
            case ActionState.ActivateKnight: this.uiGameManager.socketGameSend.confirmActivateKnight(index); break
            case ActionState.PlaceMetropolis: this.uiGameManager.socketGameSend.confirmPlaceMetropolis(index); break
            case ActionState.SelectCityToPillage: this.uiGameManager.socketGameSend.confirmPillageCity(index); break
            case ActionState.SelectKnightToTakeAction: this.uiGameManager.socketGameSend.confirmSelectKnightToTakeAction(index); break
            case ActionState.SelectKnightAction: this.uiGameManager.socketGameSend.confirmMoveKnight(index); break
            case ActionState.MoveKnightOffTurn: this.uiGameManager.socketGameSend.confirmMoveKnightOffTurn(index); break
            case ActionState.SelectKnightToRemove: this.uiGameManager.socketGameSend.confirmRemoveKnight(index); break
            case ActionState.IntrigueSelectKnightToMove: this.uiGameManager.socketGameSend.confirmRemoveKnight(index); break
            default: break
        }
    }

    private getConfirmationParameters(): UIGameActionConfirmationParameters[] {
        const confirmationParameters: UIGameActionConfirmationParameters[] = []
        confirmationParameters.push({
            texture: this.getConfirmationTexture(),
            canTakeAction: true,
            action: (placementIndex: number) => this.confirmedCornerSelection(placementIndex),
            bankActionAmount: undefined,
            resourcesToBuild: [],
        })
        return confirmationParameters
    }

    private getConfirmationTexture(): PIXI.Texture {
        switch(this.uiGameManager.gameController.currentState.actionState) {
            case ActionState.InitialPlacementPlaceSettlement: return UIGameSpriteHandler.getSettlementForPlayer(this.uiGameManager.gameController.myColor)
            case ActionState.InitialPlacementPlaceCity: return UIGameSpriteHandler.getCityForPlayer(this.uiGameManager.gameController.myColor)
            default: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_check)
        }
    }
}
